import React from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { disposeSupabaseClient, hasSupabaseConfig, setSupabaseGetToken } from '@/lib/supabaseClient'
import { queryClient } from './queryClient'

export { queryClient }

export function QueryProvider({ children }: { children: React.ReactNode }) {
  if (DEMO_AUTH_ENABLED) {
    return (
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    )
  }

  if (!hasSupabaseConfig()) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-card text-card-foreground border rounded-xl p-6">
          <h1 className="text-lg font-semibold">Missing Supabase Configuration</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Set <code className="font-mono">VITE_SUPABASE_URL</code> and{' '}
            <code className="font-mono">VITE_SUPABASE_PUBLISHABLE_KEY</code> for this environment.
          </p>
        </div>
      </div>
    )
  }

  return <ClerkQueryProvider>{children}</ClerkQueryProvider>
}

function ClerkQueryProvider({ children }: { children: React.ReactNode }) {
  const { getToken } = useClerkAuth()

  React.useLayoutEffect(() => {
    const tokenFn = getToken as (opts?: { template: string }) => Promise<string | null>
    setSupabaseGetToken(tokenFn)
    return () => {
      setSupabaseGetToken(null)
      void disposeSupabaseClient()
    }
  }, [getToken])

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}
